import { useState, useEffect } from 'react';
import Axios from 'axios';
import url from '../constants/urlsPaths';

export default function AdminNotifications({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({ pinRequests: true, errors: true, pageVisits: false, pdfClicks: false });
  const [showPrefs, setShowPrefs] = useState(false); // collapsed by default
  const [expandedIds, setExpandedIds] = useState([]);

  const token = (() => { try { return JSON.parse(sessionStorage.getItem('token')); } catch (e) { return null; } })();

  const copyToClipboard = async (text) => {
    try {
      if (!text) return;
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const t = document.createElement('textarea');
        t.value = text;
        document.body.appendChild(t);
        t.select();
        document.execCommand('copy');
        t.remove();
      }
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const exportJson = () => {
    try {
      const blob = new Blob([JSON.stringify(notifications || [], null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const name = `notifications-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.json`;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
      alert('Failed to export notifications');
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await Axios.get(url.base + '/admin/notifications', { headers: { 'x-access-token': token } });
      if (res.data && Array.isArray(res.data.notifications)) {
        setNotifications(res.data.notifications);
      }
    } catch (e) {
      console.error('Failed to fetch notifications', e);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationPrefs = async () => {
    try {
      setPrefsLoading(true);
      const res = await Axios.get(url.base + '/push/preferences', { headers: { 'x-access-token': token } });
      if (res.data && res.data.preferences) setNotificationPrefs(res.data.preferences);
    } catch (e) {
      console.error('Failed to fetch notification preferences', e);
    } finally { setPrefsLoading(false); }
  };

  useEffect(() => {
    fetchNotifications();
    fetchNotificationPrefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRead = async (id, read) => {
    try {
      // optimistic
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: !!read } : n));
      await Axios.post(url.base + '/admin/notifications/mark', { id, read: !!read }, { headers: { 'x-access-token': token } });
      // refresh to get authoritative data
      await fetchNotifications();
      // update service worker badge/count
      if (navigator && navigator.serviceWorker && navigator.serviceWorker.ready) {
        try {
          const reg = await navigator.serviceWorker.ready;
          if (reg && reg.active) reg.active.postMessage({ type: 'UPDATE_BADGE' });
        } catch (err) {
          // ignore
        }
      }
    } catch (e) {
      console.error('Failed to mark read', e);
    }
  };

  const markAllRead = async () => {
    try {
      for (const n of notifications.filter(n => !n.read)) {
        await Axios.post(url.base + '/admin/notifications/mark', { id: n.id, read: true }, { headers: { 'x-access-token': token } });
      }
      await fetchNotifications();
      // update badge after marking all read
      if (navigator && navigator.serviceWorker && navigator.serviceWorker.ready) {
        try {
          const reg = await navigator.serviceWorker.ready;
          if (reg && reg.active) reg.active.postMessage({ type: 'UPDATE_BADGE' });
        } catch (err) {
          // ignore
        }
      }
    } catch (e) {
      console.error('Failed to mark all read', e);
    }
  };

  const handleUpdateNotificationPrefs = async () => {
    try {
      setPrefsLoading(true);
      await Axios.post(url.base + '/push/preferences', { preferences: notificationPrefs }, { headers: { 'x-access-token': token } });
      alert('Notification preferences updated successfully!');
    } catch (e) {
      console.error('Failed to update notification preferences', e);
      alert('Failed to update notification preferences');
    } finally { setPrefsLoading(false); }
  };

  return (
    <div className="Modal-backdrop" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', zIndex: 10000, paddingTop: 20 }} onClick={onClose}>
      <div className="Admin-notifications-page" style={{ width: '95%', maxWidth: 900, marginTop: 12, padding: 16, borderRadius: 8, background: '#fff' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Notifications</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={markAllRead} style={{ padding: '6px 10px' }}>Mark all read</button>
            <button onClick={exportJson} style={{ padding: '6px 10px' }}>Export JSON</button>
            <button onClick={onClose} style={{ padding: '6px 10px' }}>Close</button>
          </div>
        </div>

        <div style={{ marginTop: 12, marginBottom: 12 }}>
          {/* Preferences box full width above notifications */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ padding: 12, border: '1px solid #eee', borderRadius: 6, background: '#fafafa', maxWidth: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowPrefs(prev => !prev)}>
                <h4 style={{ marginTop: 0 }}>Notification Preferences</h4>
                <div style={{ fontSize: 12, color: '#007bff' }}>{showPrefs ? 'Hide' : 'Show'}</div>
              </div>
              {showPrefs && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
                    <input type="checkbox" checked={!!notificationPrefs.pinRequests} onChange={e => setNotificationPrefs(prev => ({ ...prev, pinRequests: e.target.checked }))} />
                    <div>
                      <div style={{ fontWeight: 500 }}>PIN Code Requests</div>
                      <div style={{ fontSize: 13, color: '#666' }}>When users request new PIN codes</div>
                    </div>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
                    <input type="checkbox" checked={!!notificationPrefs.errors} onChange={e => setNotificationPrefs(prev => ({ ...prev, errors: e.target.checked }))} />
                    <div>
                      <div style={{ fontWeight: 500 }}>Error Reports</div>
                      <div style={{ fontSize: 13, color: '#666' }}>When frontend errors occur</div>
                    </div>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
                    <input type="checkbox" checked={!!notificationPrefs.pageVisits} onChange={e => setNotificationPrefs(prev => ({ ...prev, pageVisits: e.target.checked }))} />
                    <div>
                      <div style={{ fontWeight: 500 }}>Page Visits</div>
                      <div style={{ fontSize: 13, color: '#666' }}>When users visit pages (high volume)</div>
                    </div>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
                    <input type="checkbox" checked={!!notificationPrefs.pdfClicks} onChange={e => setNotificationPrefs(prev => ({ ...prev, pdfClicks: e.target.checked }))} />
                    <div>
                      <div style={{ fontWeight: 500 }}>PDF Button Clicks</div>
                      <div style={{ fontSize: 13, color: '#666' }}>When users click PDF export buttons</div>
                    </div>
                  </label>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, width: '100%' }}>
                    <button onClick={fetchNotificationPrefs} disabled={prefsLoading} style={{ padding: '8px 12px' }}>Refresh</button>
                    <button onClick={handleUpdateNotificationPrefs} disabled={prefsLoading} style={{ padding: '8px 12px', background: '#007bff', color: '#fff', border: 'none' }}>{prefsLoading ? 'Saving...' : 'Save'}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Notifications list below, scrollable and responsive */}
          <div style={{ width: '100%', minHeight: 320, maxHeight: '60vh', overflowY: 'auto', background: '#fff', borderRadius: 6, border: '1px solid #eee', boxSizing: 'border-box', padding: '12px 0 12px 0' }}>
            <h4 style={{ margin: '6px 0' }}>Received notifications</h4>
            {loading ? (
              <div>Loading...</div>
            ) : notifications.length === 0 ? (
              <div style={{ color: '#666' }}>No notifications found.</div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {notifications.map(n => (
                  <div key={n.id} style={{ border: '1px solid #eee', padding: 10, borderRadius: 6, background: n.read ? '#fafafa' : '#fff', minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{n.subject || 'Notification'}</div>
                        <div style={{ color: '#333', marginTop: 6, wordBreak: 'break-word' }}>{n.message}</div>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: 140 }}>
                        <div style={{ fontSize: 12, color: '#666' }}>{n.timestamp ? new Date(n.timestamp).toLocaleString() : ''}</div>
                        <div style={{ marginTop: 8 }}>
                          {!n.read && <span style={{ background: '#007bff', color: '#fff', padding: '4px 6px', borderRadius: 4, fontSize: 12, marginRight: 8 }}>Unread</span>}
                          <button onClick={() => toggleRead(n.id, !n.read)} style={{ padding: '6px 8px' }}>{n.read ? 'Mark unread' : 'Mark read'}</button>
                          <button onClick={() => setExpandedIds(prev => prev.includes(n.id) ? prev.filter(x => x !== n.id) : [...prev, n.id])} style={{ padding: '6px 8px', marginLeft: 8 }}>{expandedIds.includes(n.id) ? 'Hide details' : 'Details'}</button>
                        </div>
                      </div>
                    </div>
                    {expandedIds.includes(n.id) && (
                      <div style={{ marginTop: 10, fontSize: 13, color: '#444' }}>
                        <div style={{ marginBottom: 6 }}>
                          <strong>Endpoint:</strong>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, marginLeft: 8 }}>{n.endpoint ? (n.endpoint.length > 120 ? n.endpoint.substring(0, 120) + '...' : n.endpoint) : 'N/A'}</span>
                          {n.endpoint && <button onClick={() => copyToClipboard(n.endpoint)} style={{ marginLeft: 8, padding: '4px 6px' }}>Copy</button>}
                        </div>
                        {n.response && <div style={{ marginBottom: 6 }}><strong>Response:</strong> <button onClick={() => copyToClipboard(JSON.stringify(n.response))} style={{ marginLeft: 8, padding: '4px 6px' }}>Copy</button><pre style={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto' }}>{JSON.stringify(n.response, null, 2)}</pre></div>}
                        {n.error && <div style={{ marginBottom: 6 }}><strong>Error:</strong> <button onClick={() => copyToClipboard(String(n.error))} style={{ marginLeft: 8, padding: '4px 6px' }}>Copy</button><pre style={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto' }}>{String(n.error)}</pre></div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
